import { computed, watch, type App, type Ref } from 'vue'
import { criarHashContextoCatalogo } from '@next/compartilhados/catalogos/catalogo.chave'
import type { ContextoDeCatalogo } from '@next/compartilhados/catalogos'
import { revalidarCatalogosAposEscrita } from './revalidar-apos-escrita'

type Dispatch = Parameters<typeof revalidarCatalogosAposEscrita>[0]
type Contexto = Readonly<Ref<ContextoDeCatalogo | null>>
type RegistroDaFila = { id?: unknown, name?: unknown, ready?: unknown }
type SnapshotDaFila = { data?: unknown, error?: unknown }

const CATALOGOS_POR_IMPORTADOR: ReadonlyMap<string, readonly string[]> = new Map([
  ['importacao_importador_de_empregados', ['employees', 'users']],
  ['importacao_importador_de_usuarios', ['users']],
  ['importacao_importador_de_empregadores', ['employers']],
  ['importacao_importador_de_locais_de_trabalho', ['companies']],
])

const catalogosDoRegistro = (registro: RegistroDaFila): readonly string[] => {
  if (typeof registro.name !== 'string') return []
  // O produtor acrescenta o percentual ao nome tecnico do UserFile.
  const nomeBase = registro.name.replace(/ - (?:\d{2}|100)%$/u, '')
  return CATALOGOS_POR_IMPORTADOR.get(nomeBase) ?? []
}

export const criarObservadorImportacoesConcluidas = (contexto: Contexto, dispatch: Dispatch) => {
  const chaveContexto = computed(() => contexto.value ? criarHashContextoCatalogo(contexto.value) : null)
  const importacoesTratadas = new Set<string>()
  let geracao = 0
  let ativo = true
  const pararContexto = watch(chaveContexto, chave => {
    geracao++
    if (!chave) importacoesTratadas.clear()
  }, { flush: 'sync' })

  const prepararObservacao = () => {
    const chaveDaConsulta = chaveContexto.value
    const geracaoDaConsulta = geracao

    return (snapshot: SnapshotDaFila) => {
      if (!ativo || !chaveDaConsulta || geracaoDaConsulta !== geracao) return
      const registros: unknown[] = [
        ...(Array.isArray(snapshot?.data) ? snapshot.data : []),
        ...(Array.isArray(snapshot?.error) ? snapshot.error : []),
      ]
      const catalogos = new Set<string>()
      for (const item of registros) {
        if (!item || typeof item !== 'object') continue
        const registro = item as RegistroDaFila
        if (registro.ready !== true || typeof registro.id !== 'string' || !registro.id.trim()) continue
        const modulos = catalogosDoRegistro(registro)
        if (!modulos.length) continue
        const identidade = JSON.stringify([chaveDaConsulta, registro.id])
        if (importacoesTratadas.has(identidade)) continue
        importacoesTratadas.add(identidade)
        modulos.forEach(modulo => catalogos.add(modulo))
      }
      revalidarCatalogosAposEscrita(dispatch, [...catalogos])
    }
  }

  return {
    prepararObservacao,
    parar: () => { ativo = false; pararContexto(); importacoesTratadas.clear() },
  }
}

export const instalarRevalidacaoCatalogosPorImportacao = (app: App, contexto: Contexto, dispatch: Dispatch): void => {
  const observador = criarObservadorImportacoesConcluidas(contexto, dispatch)
  app.provide('prepararObservacaoImportacoes', observador.prepararObservacao)
  app.onUnmount(observador.parar)
}
